import React, { useState } from 'react';
import { ArrowLeft, Lock, CreditCard, ShieldCheck, Check } from 'lucide-react';
import { toast } from 'sonner';
import MorphButton from '../components/MorphButton';
import AppLogo from '../components/AppLogo';
import { requireAuth } from '../components/AuthGate';

// Wording that makes the billing cadence unambiguous before anyone pays.
const CADENCE_COPY = {
  daily: { suffix: '/ day', note: 'Charged every morning after Subuh. Pause anytime.' },
  weekly: { suffix: '/ week', note: 'Charged every Friday. Pause anytime.' },
  monthly: { suffix: '/ month', note: 'Charged on this date each month. Pause anytime.' },
  once: { suffix: '', note: 'A single payment. Nothing recurring, nothing to cancel.' }
};

const formatCard = (v) =>
  v.replace(/\D/g, '').slice(0, 16).replace(/(.{4})/g, '$1 ').trim();

const formatExpiry = (v) => {
  const d = v.replace(/\D/g, '').slice(0, 4);
  return d.length > 2 ? `${d.slice(0, 2)}/${d.slice(2)}` : d;
};

/**
 * Mocked Stripe Checkout. Deliberately mirrors Stripe's hosted page — merchant
 * column on the left, card form on the right — so stakeholders can judge the
 * real payment experience. Nothing is transmitted and no card is validated
 * beyond shape; this is a prototype with no payment processor behind it.
 */
export default function StripeCheckout({ option, onBack, onDone }) {
  const cadence = CADENCE_COPY[option.cadence] || CADENCE_COPY.once;
  const [amount, setAmount] = useState(option.amounts[1]);
  const [custom, setCustom] = useState('');
  const [customActive, setCustomActive] = useState(false);
  const [card, setCard] = useState('');
  const [expiry, setExpiry] = useState('');
  const [cvc, setCvc] = useState('');
  const [name, setName] = useState('');

  const effective = customActive ? Number(custom) || 0 : amount;

  const validate = () => {
    if (effective < 1) {
      toast.error('Enter an amount of at least $1', { position: 'top-center' });
      return false;
    }
    if (card.replace(/\s/g, '').length < 16) {
      toast.error('Enter a valid 16-digit card number', { position: 'top-center' });
      return false;
    }
    if (expiry.length < 5) {
      toast.error('Enter the card expiry as MM/YY', { position: 'top-center' });
      return false;
    }
    if (cvc.length < 3) {
      toast.error('Enter the 3-digit CVC', { position: 'top-center' });
      return false;
    }
    if (!name.trim()) {
      toast.error('Enter the name on the card', { position: 'top-center' });
      return false;
    }
    return true;
  };

  const field =
    'w-full rounded-lg border border-border bg-white px-3 py-2.5 text-sm placeholder:text-muted-foreground focus:outline-none focus:ring-2 focus:ring-primary/40';

  return (
    <div className="min-h-screen bg-white md:bg-[#F3F6F5] page-fade-in" data-testid="stripe-checkout-page">
      <div className="bg-white px-4 pt-4 pb-3 flex items-center gap-3 border-b border-border/60 md:border-none">
        <button type="button" data-testid="checkout-back" onClick={onBack} aria-label="Back">
          <ArrowLeft className="h-5 w-5 text-[#0F172A]" />
        </button>
        <span className="text-base font-bold text-[#0F172A]">Checkout</span>
        <span className="ml-auto flex items-center gap-1 text-[11px] font-semibold text-muted-foreground">
          <Lock className="h-3 w-3" /> Secure
        </span>
      </div>

      <div className="md:max-w-5xl md:mx-auto md:my-8 md:grid md:grid-cols-2 md:gap-0 md:overflow-hidden md:rounded-2xl md:border md:border-border/60 md:bg-white md:shadow-lg">
        {/* Merchant summary */}
        <div className="px-4 py-5 md:px-8 md:py-10 md:bg-[#FAFBFB] md:border-r md:border-border/60">
          <div className="flex items-center gap-2">
            <AppLogo size="sm" />
            <span className="text-sm font-bold text-[#0F172A]">MTFA IhsanOne</span>
          </div>

          <p className="mt-6 text-sm text-muted-foreground">{option.title}</p>
          <div className="mt-1 flex items-end gap-1.5">
            <span className="text-3xl md:text-4xl font-extrabold text-[#0F172A]" data-testid="checkout-amount">
              ${effective.toLocaleString()}
            </span>
            {cadence.suffix && (
              <span className="pb-1 text-sm font-semibold text-muted-foreground">{cadence.suffix}</span>
            )}
          </div>
          <p className="mt-2 text-xs leading-snug text-muted-foreground">{cadence.note}</p>

          <div className="mt-6">
            <p className="text-xs font-semibold text-[#0F172A]">Choose an amount</p>
            <div className="mt-2 grid grid-cols-4 gap-2">
              {option.amounts.map((a) => {
                const active = !customActive && amount === a;
                return (
                  <button
                    key={a}
                    type="button"
                    data-testid={`checkout-amount-${a}`}
                    onClick={() => { setAmount(a); setCustomActive(false); }}
                    aria-pressed={active}
                    className={`rounded-lg border py-2.5 text-sm font-bold transition-colors ${
                      active
                        ? 'border-primary bg-primary text-white'
                        : 'border-border bg-white text-[#0F172A] hover:border-primary/60'
                    }`}
                  >
                    ${a}
                  </button>
                );
              })}
            </div>

            <div className="mt-2 flex items-center gap-2">
              <span className="text-sm font-semibold text-muted-foreground">$</span>
              <input
                type="text"
                inputMode="decimal"
                data-testid="checkout-custom-amount"
                value={custom}
                onFocus={() => setCustomActive(true)}
                onChange={(e) => {
                  setCustom(e.target.value.replace(/[^\d.]/g, ''));
                  setCustomActive(true);
                }}
                placeholder="Other amount"
                className={`${field} ${customActive ? 'ring-2 ring-primary/40' : ''}`}
              />
            </div>
          </div>

          <div className="mt-6 flex items-start gap-2 rounded-lg bg-primary/5 p-3">
            <ShieldCheck className="mt-0.5 h-4 w-4 flex-shrink-0 text-primary" />
            <p className="text-[11px] leading-snug text-muted-foreground">
              Donations to MTFA qualify for 2.5&times; tax deduction. Your receipt is emailed
              automatically.
            </p>
          </div>
        </div>

        {/* Card form */}
        <div className="px-4 py-5 md:px-8 md:py-10">
          <p className="text-sm font-bold text-[#0F172A]">Pay with card</p>

          <div className="mt-4 flex flex-col gap-3">
            <div>
              <label className="text-xs font-semibold text-[#0F172A]" htmlFor="checkout-card">
                Card number
              </label>
              <div className="relative mt-1.5">
                <input
                  id="checkout-card"
                  data-testid="checkout-card-number"
                  value={card}
                  onChange={(e) => setCard(formatCard(e.target.value))}
                  placeholder="4242 4242 4242 4242"
                  inputMode="numeric"
                  autoComplete="off"
                  className={`${field} pr-10`}
                />
                <CreditCard className="absolute right-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
              </div>
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label className="text-xs font-semibold text-[#0F172A]" htmlFor="checkout-expiry">
                  Expiry
                </label>
                <input
                  id="checkout-expiry"
                  data-testid="checkout-expiry"
                  value={expiry}
                  onChange={(e) => setExpiry(formatExpiry(e.target.value))}
                  placeholder="MM/YY"
                  inputMode="numeric"
                  autoComplete="off"
                  className={`${field} mt-1.5`}
                />
              </div>
              <div>
                <label className="text-xs font-semibold text-[#0F172A]" htmlFor="checkout-cvc">
                  CVC
                </label>
                <input
                  id="checkout-cvc"
                  data-testid="checkout-cvc"
                  value={cvc}
                  onChange={(e) => setCvc(e.target.value.replace(/\D/g, '').slice(0, 3))}
                  placeholder="123"
                  inputMode="numeric"
                  autoComplete="off"
                  className={`${field} mt-1.5`}
                />
              </div>
            </div>

            <div>
              <label className="text-xs font-semibold text-[#0F172A]" htmlFor="checkout-name">
                Name on card
              </label>
              <input
                id="checkout-name"
                data-testid="checkout-name"
                value={name}
                onChange={(e) => setName(e.target.value)}
                placeholder="As printed on the card"
                className={`${field} mt-1.5`}
              />
            </div>
          </div>

          <MorphButton
            data-testid="checkout-pay-button"
            idleLabel={`Pay $${effective.toLocaleString()}${cadence.suffix ? ` ${cadence.suffix}` : ''}`}
            successLabel="Payment Received"
            onClick={async () => {
              if (!validate()) return false;
              return requireAuth();
            }}
            onComplete={() => onDone?.({ optionId: option.id, amount: effective })}
            className="mt-5 w-full h-12 rounded-xl bg-primary text-white font-bold text-sm hover:bg-primary-hover"
          />

          <p className="mt-3 flex items-center justify-center gap-1.5 text-[11px] text-muted-foreground">
            <Lock className="h-3 w-3" />
            Mocked checkout — no card details are sent anywhere
          </p>

          <div className="mt-5 flex flex-wrap items-center justify-center gap-x-4 gap-y-1.5 text-[11px] text-muted-foreground">
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3 text-primary" /> PCI compliant
            </span>
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3 text-primary" /> 3D Secure
            </span>
            <span className="flex items-center gap-1">
              <Check className="h-3 w-3 text-primary" /> Powered by Stripe
            </span>
          </div>
        </div>
      </div>
    </div>
  );
}
